'use client';
import { useState } from 'react';
import Link from 'next/link';
import { Send, MessageCircle, AlertTriangle, Share2, X } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { useMessagesStore } from '@/store/messages.store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { getDisplayName } from '@/lib/displayName';

const URGENCY_CFG: Record<string, { label: string; color: string; bg: string }> = {
  urgent:     { label: '🔴 Срочно',          color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
  this_week:  { label: '🟠 На этой неделе',  color: '#ea580c', bg: 'rgba(234,88,12,0.08)' },
  this_month: { label: '🟡 В этом месяце',   color: '#ca8a04', bg: 'rgba(202,138,4,0.08)'  },
  flexible:   { label: '🟢 Гибкий срок',     color: '#16a34a', bg: 'rgba(22,163,74,0.08)'  },
  today:      { label: '🔴 Сегодня',         color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
};

const STATUS_CFG: Record<string, { label: string; color: string }> = {
  OPEN:      { label: 'Открыт',   color: '#307479' },
  REVIEWING: { label: 'В работе', color: '#2563eb' },
};

interface Props {
  req: any;
  isSpecialist: boolean;
  specialistId: string | null;
  onRespond: (id: string) => void;
  respondingId: string | null;
  onCancelRespond: () => void;
  price: string;
  comment: string;
  onPriceChange: (v: string) => void;
  onCommentChange: (v: string) => void;
  onSubmitRespond: (id: string) => void;
  respondIsPending: boolean;
}

export default function RequestCatalogCard({
  req, isSpecialist, specialistId, onRespond, respondingId,
  onCancelRespond, price, comment, onPriceChange, onCommentChange,
  onSubmitRespond, respondIsPending,
}: Props) {
  const { user, isAuthenticated } = useAuthStore();
  const { setOpenWithUserId } = useMessagesStore();
  const qc = useQueryClient();

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportComment, setReportComment] = useState('');

  const urgency = URGENCY_CFG[req.urgency] ?? { label: req.urgency, color: '#6b7280', bg: 'rgba(107,114,128,0.08)' };
  const status = STATUS_CFG[req.status] ?? { label: req.status, color: '#6b7280' };
  const isResponding = respondingId === req.id;

  // Поделиться
  const handleShare = async () => {
    const url = `${window.location.origin}/requests/${req.id}`;
    if (navigator.share) {
      await navigator.share({ title: req.title, url });
    } else {
      await navigator.clipboard.writeText(url);
      notify.success('Ссылка скопирована');
    }
  };

  // Написать заказчику
  const handleMessage = () => {
    if (!isAuthenticated()) { notify.error('Войдите чтобы написать'); return; }
    if (!req.client?.id) return;
    setOpenWithUserId(req.client.id);
  };

  // Жалоба
  const reportMutation = useMutation({
    mutationFn: () => api.post('/reports', {
      targetType: 'request',
      targetId: req.id,
      reason: reportReason,
      comment: reportComment,
    }),
    onSuccess: () => { notify.success('Жалоба отправлена'); setShowReportModal(false); setReportReason(''); setReportComment(''); },
    onError: () => notify.error('Ошибка отправки жалобы'),
  });

  return (
    <>
      <div style={{
        background: 'var(--color-bg-surface)',
        borderRadius: 16,
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
      }}>
        {/* Шапка карточки */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--color-border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Мета-строка */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>#{req.displayId}</span>
                {req.category?.name && (
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'rgba(48,116,121,0.1)', color: 'var(--color-brand)', fontWeight: 600 }}>
                    {req.category.name}
                  </span>
                )}
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: urgency.bg, color: urgency.color, fontWeight: 500 }}>
                  {urgency.label}
                </span>
                {req.status === 'REVIEWING' && (
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'rgba(37,99,235,0.08)', color: '#2563eb', fontWeight: 500 }}>
                    В работе
                  </span>
                )}
              </div>
              {/* Заголовок */}
              <Link href={`/requests/${req.id}`} style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                className="hover:text-brand transition-colors">
                {req.title}
              </Link>
              {req.description && (
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {req.description}
                </p>
              )}
            </div>
            {/* Бюджет */}
            {req.budget > 0 && (
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--color-text-primary)' }}>
                  {Number(req.budget).toLocaleString('ru-RU')} ₽
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Инфо-строка */}
        <div style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', borderBottom: '1px solid var(--color-border-subtle)' }}>
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            👤 {getDisplayName(req.client)}
          </span>
          {req.client?.city && (
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
              📍 {req.client.city}
            </span>
          )}
          {req.preferred_date ? (
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
              📅 {new Date(req.preferred_date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              {new Date(req.createdAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
            </span>
          )}
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            💬 {req.responsesCount} {req.responsesCount === 1 ? 'отклик' : req.responsesCount < 5 ? 'отклика' : 'откликов'}
          </span>
        </div>

        {/* Действия */}
        <div style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {/* Левые кнопки */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {isAuthenticated() && req.client?.id && req.client.id !== user?.id && (
              <button onClick={handleMessage} title="Написать заказчику"
                style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--color-text-secondary)', transition: 'all 0.15s' }}
                className="hover:border-brand hover:text-brand">
                <MessageCircle size={15} />
              </button>
            )}
            <button onClick={handleShare} title="Поделиться"
              style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--color-text-secondary)', transition: 'all 0.15s' }}
              className="hover:border-brand hover:text-brand">
              <Share2 size={15} />
            </button>
            {isAuthenticated() && req.client?.id !== user?.id && (
              <button onClick={() => setShowReportModal(true)} title="Пожаловаться"
                style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--color-text-secondary)', transition: 'all 0.15s' }}
                className="hover:border-red-400 hover:text-red-400">
                <AlertTriangle size={15} />
              </button>
            )}
          </div>

          {/* Кнопка Откликнуться */}
          {!isResponding && req.status === 'OPEN' && isSpecialist && (
            <button onClick={() => onRespond(req.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 10, background: 'var(--color-brand)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'opacity 0.15s' }}
              className="hover:opacity-90">
              <Send size={13} />
              Откликнуться
            </button>
          )}
        </div>

        {/* Форма отклика */}
        {isResponding && (
          <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--color-border-subtle)', paddingTop: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Ваша цена (₽)</label>
                <input type="number" value={price} onChange={e => onPriceChange(e.target.value)} placeholder="Введите сумму"
                  style={{ width: '100%', border: '1px solid var(--color-border)', borderRadius: 10, padding: '8px 12px', fontSize: 14, background: 'var(--color-bg-subtle)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Комментарий</label>
                <textarea value={comment} onChange={e => onCommentChange(e.target.value)} placeholder="Расскажите о своём опыте..." rows={2}
                  style={{ width: '100%', border: '1px solid var(--color-border)', borderRadius: 10, padding: '8px 12px', fontSize: 14, background: 'var(--color-bg-subtle)', color: 'var(--color-text-primary)', outline: 'none', resize: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={onCancelRespond}
                  style={{ padding: '7px 14px', borderRadius: 10, border: '1px solid var(--color-border)', background: 'transparent', fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                  Отмена
                </button>
                <button onClick={() => onSubmitRespond(req.id)} disabled={!price || respondIsPending}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 10, background: 'var(--color-brand)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!price || respondIsPending) ? 0.5 : 1 }}>
                  <Send size={13} />
                  {respondIsPending ? 'Отправка...' : 'Отправить'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Модалка жалобы */}
      {showReportModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', padding: 16 }}>
          <div style={{ background: 'var(--color-bg-surface)', borderRadius: 16, border: '1px solid var(--color-border)', padding: 24, width: '100%', maxWidth: 420 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>Пожаловаться на заказ</h3>
              <button onClick={() => setShowReportModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <select value={reportReason} onChange={e => setReportReason(e.target.value)}
                style={{ width: '100%', border: '1px solid var(--color-border)', borderRadius: 10, padding: '8px 12px', fontSize: 14, background: 'var(--color-bg-subtle)', color: 'var(--color-text-primary)', outline: 'none' }}>
                <option value="">Выберите причину</option>
                <option value="spam">Спам</option>
                <option value="inappropriate">Неприемлемый контент</option>
                <option value="fake">Мошенничество</option>
                <option value="duplicate">Дубликат</option>
                <option value="other">Другое</option>
              </select>
              <textarea value={reportComment} onChange={e => setReportComment(e.target.value)} placeholder="Комментарий (необязательно)" rows={3}
                style={{ width: '100%', border: '1px solid var(--color-border)', borderRadius: 10, padding: '8px 12px', fontSize: 14, background: 'var(--color-bg-subtle)', color: 'var(--color-text-primary)', outline: 'none', resize: 'none' }} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowReportModal(false)}
                  style={{ padding: '7px 14px', borderRadius: 10, border: '1px solid var(--color-border)', background: 'transparent', fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                  Отмена
                </button>
                <button onClick={() => reportMutation.mutate()} disabled={!reportReason || reportMutation.isPending}
                  style={{ padding: '7px 16px', borderRadius: 10, background: '#dc2626', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!reportReason || reportMutation.isPending) ? 0.5 : 1 }}>
                  Отправить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
